// @ts-check
import { Archive, ArchiveRestore, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { formatDay, label, STAGE_ORDER } from '../../utils/wholesale';
import ContactModal from './ContactModal';
import PartnerFormModal from './PartnerFormModal';
import { card, DeliveryNote, EnumSelect, input, primaryBtn, secondaryBtn } from './shared';

const Row = ({ name, children }) =>
  children ? (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-border last:border-0">
      <dt className="text-sm text-muted-foreground">{name}</dt>
      <dd className="col-span-2 text-sm whitespace-pre-wrap break-words">{children}</dd>
    </div>
  ) : null;

/** @param {{partner: any, onChanged: () => void}} props */
const OverviewTab = ({ partner, onChanged }) => {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [contactModal, setContactModal] = useState(undefined); // undefined closed, null adding, object editing
  const [nextStage, setNextStage] = useState(partner.stage);
  const [note, setNote] = useState('');
  const [moving, setMoving] = useState(false);

  const moveStage = async () => {
    setMoving(true);
    try {
      await adminWholesaleService.changeStage(partner.id, nextStage, note.trim() || undefined);
      toast.success(`Moved to ${label(nextStage)}`);
      setNote('');
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Could not change the stage'); // names the evidence still missing
    } finally {
      setMoving(false);
    }
  };

  const toggleArchive = async () => {
    try {
      await adminWholesaleService.updatePartner(partner.id, { is_archived: !partner.is_archived });
      toast.success(partner.is_archived ? 'Partner restored' : 'Partner archived');
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Failed to update the partner');
    }
  };

  const removeContact = async (contact) => {
    if (!window.confirm(`Remove ${contact.name || 'this contact'}? Messages already logged are kept.`)) return;
    try {
      await adminWholesaleService.deleteContact(contact.id);
      onChanged();
    } catch (err) {
      toast.error(err.message || 'Failed to remove the contact');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <DeliveryNote delivery={partner.email_delivery} />
      <div className="lg:col-span-2 space-y-6">
        <div className={`${card} p-5`}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">Details</h2>
            <div className="flex gap-2">
              <button onClick={() => setEditing(true)} className={`${secondaryBtn} flex items-center gap-1.5`}>
                <Pencil size={14} /> Edit
              </button>
              <button onClick={toggleArchive} className={`${secondaryBtn} flex items-center gap-1.5`}>
                {partner.is_archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                {partner.is_archived ? 'Restore' : 'Archive'}
              </button>
            </div>
          </div>
          <dl>
            <Row name="Kind">{label(partner.kind)}</Row>
            <Row name="Store price tier">{label(partner.price_tier)}</Row>
            <Row name="Capsule">{label(partner.capsule_tier)}</Row>
            <Row name="Lead with">{[label(partner.lead_model), partner.lead_note].filter(Boolean).join(': ')}</Row>
            <Row name="Priority">{label(partner.priority)}</Row>
            <Row name="Next action">
              {partner.next_action
                ? `${partner.next_action}${partner.next_action_on ? ` (${formatDay(partner.next_action_on)})` : ''}`
                : null}
            </Row>
            <Row name="Event starts">{partner.event_starts_on ? formatDay(partner.event_starts_on) : null}</Row>
            <Row name="Apply by">{partner.apply_by ? formatDay(partner.apply_by) : null}</Row>
            <Row name="Same owner group">{partner.owner_group}</Row>
            <Row name="Applies through">
              {partner.application_first && partner.application_url ? 'A web form or application' : null}
            </Row>
            <Row name="Application link">
              {partner.application_url ? (
                /^https?:\/\/(?![^\s/?#]*@)[^\s]+$/i.test(partner.application_url) ? (
                  <a className="underline" href={partner.application_url} target="_blank" rel="noopener noreferrer">
                    {partner.application_url}
                  </a>
                ) : (
                  partner.application_url
                )
              ) : null}
            </Row>
            <Row name="What they ask for">{partner.application_checklist}</Row>
            <Row name="City">{[partner.city, partner.state].filter(Boolean).join(', ')}</Row>
            <Row name="Website">{partner.website}</Row>
            <Row name="Instagram">{partner.instagram_handle}</Row>
            <Row name="Source">{partner.source}</Row>
            <Row name="First-mail subject">{partner.first_mail_subject}</Row>
            <Row name="Custom paragraph">{partner.custom_paragraph}</Row>
            <Row name="Legal name">{partner.legal_name}</Row>
            <Row name="GSTIN">{partner.gstin}</Row>
            <Row name="Udyam">{partner.udyam_number}</Row>
            <Row name="Address">{partner.address}</Row>
            <Row name="Notes">{partner.notes}</Row>
          </dl>
        </div>

        <div className={`${card} p-5`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Contacts</h2>
            <button onClick={() => setContactModal(null)} className={`${secondaryBtn} flex items-center gap-1.5`}>
              <Plus size={14} /> Add contact
            </button>
          </div>
          {partner.contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No contacts yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {partner.contacts.map((c) => (
                <li key={c.id} className="flex items-start gap-3 py-3">
                  <div className="flex-1 min-w-0 text-sm">
                    <p className="font-medium">
                      {c.name || 'Unnamed'}
                      {c.role ? <span className="text-muted-foreground font-normal"> · {c.role}</span> : null}
                      {c.is_decision_maker ? (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300">
                          decides
                        </span>
                      ) : null}
                    </p>
                    <p className="text-muted-foreground break-words">
                      {[c.email, c.phone, c.whatsapp && `WhatsApp ${c.whatsapp}`, c.instagram]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <p className="text-xs text-muted-foreground">Prefers {label(c.preferred_channel)}</p>
                  </div>
                  <button
                    onClick={() => setContactModal(c)}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Edit ${c.name || 'contact'}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => removeContact(c)}
                    className="text-muted-foreground hover:text-red-600"
                    aria-label={`Remove ${c.name || 'contact'}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={`${card} p-5 h-fit`}>
        <h2 className="text-base font-semibold mb-1">Stage</h2>
        <p className="text-xs text-muted-foreground mb-3">
          A door moves on evidence only: a logged send, a pasted reply, a quote, a countersigned sheet. Nothing moves on
          its own.
        </p>
        <EnumSelect value={nextStage} onChange={setNextStage} options={STAGE_ORDER} aria-label="Stage" />
        {(nextStage === 'parked' || nextStage === 'declined') && (
          <input
            className={`${input} mt-2`}
            placeholder="Reason (kept in the notes)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        <button
          onClick={moveStage}
          disabled={moving || nextStage === partner.stage}
          className={`${primaryBtn} mt-3 w-full`}
        >
          {moving ? 'Moving...' : 'Move'}
        </button>
        <p className="text-xs text-muted-foreground mt-3">
          In {label(partner.stage)} for {partner.days_in_stage} day{partner.days_in_stage === 1 ? '' : 's'}.
        </p>
      </div>

      {editing && (
        <PartnerFormModal
          partner={partner}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
      {contactModal !== undefined && (
        <ContactModal
          partnerId={partner.id}
          contact={contactModal || undefined}
          onClose={() => setContactModal(undefined)}
          onSaved={() => {
            setContactModal(undefined);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default OverviewTab;
